'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: number;
  icon: string;
  titleFr: string;
  titleDe: string;
  descriptionFr: string;
  descriptionDe: string;
  color: string;
}

interface ServicesGridProps {
  className?: string;
}

const ServicesGrid = ({ className = '' }: ServicesGridProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('fr');

  useEffect(() => {
    setIsHydrated(true);
    const savedLanguage = localStorage.getItem('preferredLanguage') || 'fr';
    setCurrentLanguage(savedLanguage);
  }, []);

  const services: Service[] = [
    {
      id: 1,
      icon: 'HomeModernIcon',
      titleFr: 'Nettoyages et entretiens',
      titleDe: 'Reinigung und Wartung',
      descriptionFr: 'Services de nettoyage réguliers pour maintenir vos espaces impeccables avec notre équipe professionnelle certifiée.',
      descriptionDe: 'Regelmäßige Reinigungsdienste, um Ihre Räume mit unserem zertifizierten professionellen Team makellos zu halten.',
      color: 'bg-blue-500',
    },
    {
      id: 2,
      icon: 'UserGroupIcon',
      titleFr: 'Services aux particuliers',
      titleDe: 'Dienstleistungen für Privatpersonen',
      descriptionFr: 'Solutions de nettoyage personnalisées pour votre maison, adaptées à vos besoins et votre emploi du temps.',
      descriptionDe: 'Maßgeschneiderte Reinigungslösungen für Ihr Zuhause, angepasst an Ihre Bedürfnisse und Ihren Zeitplan.',
      color: 'bg-purple-500',
    },
    {
      id: 3,
      icon: 'BuildingOfficeIcon',
      titleFr: 'Services de conciergerie',
      titleDe: 'Concierge-Dienstleistungen',
      descriptionFr: 'Gestion complète de l\'entretien de vos immeubles avec un service de conciergerie professionnel et fiable.',
      descriptionDe: 'Vollständige Verwaltung der Wartung Ihrer Gebäude mit professionellem und zuverlässigem Concierge-Service.',
      color: 'bg-green-500',
    },
    {
      id: 4,
      icon: 'SparklesIcon',
      titleFr: 'Nettoyages spéciaux',
      titleDe: 'Spezialreinigungen',
      descriptionFr: 'Nettoyages en profondeur et services spécialisés pour les situations nécessitant une expertise particulière.',
      descriptionDe: 'Tiefenreinigung und spezialisierte Dienstleistungen für Situationen, die besondere Expertise erfordern.',
      color: 'bg-yellow-500',
    },
    {
      id: 5,
      icon: 'WrenchScrewdriverIcon',
      titleFr: 'Multi-services',
      titleDe: 'Multi-Services',
      descriptionFr: 'Services complémentaires incluant petits travaux, jardinage et maintenance pour une solution complète.',
      descriptionDe: 'Ergänzende Dienstleistungen einschließlich kleiner Arbeiten, Gartenarbeit und Wartung für eine Komplettlösung.',
      color: 'bg-red-500',
    },
    {
      id: 6,
      icon: 'ShieldCheckIcon',
      titleFr: 'Garantie qualité',
      titleDe: 'Qualitätsgarantie',
      descriptionFr: 'Tous nos services sont garantis avec assurance complète et engagement de satisfaction client.',
      descriptionDe: 'Alle unsere Dienstleistungen sind mit vollständiger Versicherung und Kundenzufriedenheitsgarantie garantiert.',
      color: 'bg-indigo-500',
    },
  ];

  const content = {
    fr: {
      title: 'Nos services professionnels',
      subtitle: 'Des solutions complètes pour tous vos besoins de nettoyage',
      viewAll: 'Voir tous les services',
    },
    de: {
      title: 'Unsere professionellen Dienstleistungen',
      subtitle: 'Umfassende Lösungen für alle Ihre Reinigungsbedürfnisse',
      viewAll: 'Alle Dienstleistungen anzeigen',
    },
  };

  const currentContent = isHydrated ? content[currentLanguage as keyof typeof content] : content.fr;

  return (
    <section className={`py-16 lg:py-24 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            {currentContent.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {services.map((service) => (
            <div
              key={service.id}
              className="group bg-card hover:bg-surface border border-border rounded-xl p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              <div className={`w-14 h-14 ${service.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <Icon
                  name={service.icon as any}
                  size={28}
                  variant="solid"
                  className="text-white"
                />
              </div>

              <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                {isHydrated && currentLanguage === 'de' ? service.titleDe : service.titleFr}
              </h3>

              <p className="text-muted-foreground mb-4 leading-relaxed">
                {isHydrated && currentLanguage === 'de' ? service.descriptionDe : service.descriptionFr}
              </p>

              <Link
                href="/services"
                className="inline-flex items-center gap-2 text-primary hover:text-secondary font-medium text-sm transition-colors duration-300"
              >
                En savoir plus
                <Icon name="ArrowRightIcon" size={16} variant="outline" />
              </Link>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Link
            href="/services"
            className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-secondary text-primary-foreground px-8 py-4 rounded-lg font-heading font-semibold text-lg transition-all duration-300 shadow-md hover:shadow-lg"
          >
            {currentContent.viewAll}
            <Icon name="ArrowRightIcon" size={20} variant="solid" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;